import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { NavLink } from "react-router-dom";

import { cn } from "@/shared/lib/utils";
import { ADMIN_NAVIGATION } from "./admin-navigation";

export interface AdminBottomNavBarProps {
  className?: string;
}

/**
 * Floating bottom navigation bar for the Admin Portal.
 * Same treatment as the Teacher and Student bars so the portal shells stay visually consistent.
 */
export function AdminBottomNavBar({ className }: AdminBottomNavBarProps) {
  const container = useRef<HTMLDivElement>(null);
  const location = useLocation();
  useEffect(() => {
    const reveal = () =>
      (
        container.current?.querySelector<HTMLElement>("a:focus") ??
        container.current?.querySelector<HTMLElement>('[aria-current="page"]')
      )?.scrollIntoView({ block: "nearest", inline: "nearest" });
    reveal();
    const observer = new ResizeObserver(reveal);
    if (container.current) observer.observe(container.current);
    const active = container.current?.querySelector<HTMLElement>(
      '[aria-current="page"]',
    );
    // Active labels animate their width; watching the item also handles overflow after that animation.
    if (active) observer.observe(active);
    return () => observer.disconnect();
  }, [location.pathname]);
  return (
    <nav
      aria-label="ناوبری اصلی پنل مدیر"
      dir="rtl"
      className={cn(
        "pointer-events-none absolute right-0 bottom-4 left-0 z-20 flex justify-center px-4 select-none",
        className,
      )}
    >
      <div
        ref={container}
        className="pointer-events-auto flex max-w-full items-center gap-1 overflow-x-auto rounded-full border border-slate-200/90 bg-white/95 p-1.5 shadow-[0_8px_24px_rgba(0,0,0,0.1)] backdrop-blur-md"
      >
        {/* Attendance is temporarily hidden until its UI is reworked; keep its routes and content intact. */}
        {ADMIN_NAVIGATION.filter((tab) => tab.id !== "absences").map((tab) => {
          const Icon = tab.icon;

          return (
            <NavLink
              key={tab.id}
              to={tab.path}
              aria-label={tab.title}
              onFocus={(event) =>
                event.currentTarget.scrollIntoView({
                  block: "nearest",
                  inline: "nearest",
                })
              }
              className={({ isActive }) =>
                cn(
                  "focus-visible:outline-brand flex h-10 shrink-0 items-center justify-center rounded-full px-3 transition-all duration-200 focus-visible:outline-2",
                  isActive
                    ? cn(
                        tab.activeBg,
                        tab.activeColor,
                        "border font-bold",
                        tab.activeBorder,
                      )
                    : "text-slate-500 hover:bg-slate-100/80 hover:text-slate-800",
                )
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
                  {isActive && (
                    <span className="mr-1.5 text-xs tracking-tight">
                      {tab.title}
                    </span>
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
